import React, { useState } from 'react';

import { Button, Drawer, EmptyMessage, Loading } from '@components';
import { useFetch } from '@hooks';
import { getFailedBulkActionTargets } from '@api';
import NotificationItem from './NotificationItem';
import type { UseNotificationsResult } from './useNotifications';

export type NotificationsFeedProps = UseNotificationsResult;

export const NotificationsFeed: React.FC<NotificationsFeedProps> = ({
  items,
  loading,
  reloading,
  unreadCount,
  refresh,
  markAllAsRead,
}) => {
  const [open, setOpen] = useState(false);
  const [failureJobId, setFailureJobId] = useState<string | null>(null);

  const failures = useFetch(
    () => getFailedBulkActionTargets(failureJobId as string),
    [failureJobId],
    { skip: !failureJobId, onError: false }
  );

  const handleOpen = () => {
    setOpen(true);
    markAllAsRead();
  };

  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <>
      <div className="relative">
        <Button
          variant="icon"
          icon="bell"
          size="lg"
          tooltip={
            unreadCount > 0
              ? `Notifications (${unreadCount} unread)`
              : 'Notifications'
          }
          className="size-[3.4rem] rounded-sm text-small"
          onClick={handleOpen}
        />
        {unreadCount > 0 && (
          <span className="pointer-events-none absolute -right-[0.15rem] -top-[0.15rem] flex min-h-[1.6rem] min-w-[1.6rem] items-center justify-center rounded-full bg-danger px-[0.35rem] text-[0.9rem] font-bold leading-none text-white">
            {badgeLabel}
          </span>
        )}
      </div>

      <Drawer
        isOpen={open}
        onClose={() => {
          setOpen(false);
          setFailureJobId(null);
        }}
        width="440px"
        className="z-[101]"
        header={
          <div className="flex items-center justify-between gap-md">
            <div>
              <div className="font-semibold text-medium">Notifications</div>
              <div className="text-tiny opacity-60">
                Organization activity and bulk actions
              </div>
            </div>
            <Button
              variant="icon"
              icon="refresh"
              loading={reloading}
              tooltip="Refresh"
              onClick={refresh}
            />
          </div>
        }
      >
        <div className="flex min-w-0 flex-1 flex-col gap-md">
          {failureJobId && (
            <section className="border-b border-border pb-md">
              <div className="mb-sm flex items-center justify-between gap-sm">
                <div className="font-semibold">Failed contacts</div>
                <Button
                  size="xs"
                  type="ghost"
                  icon="left"
                  onClick={() => setFailureJobId(null)}
                >
                  Back to feed
                </Button>
              </div>
              {failures.loading && <Loading />}
              {!failures.loading && !failures.data?.items.length && (
                <EmptyMessage>No failed contacts found.</EmptyMessage>
              )}
              <div className="flex flex-col gap-sm">
                {failures.data?.items.map(target => (
                  <div
                    key={target.contact_id}
                    className="rounded-xs border border-border p-sm"
                  >
                    <div className="break-all font-medium text-tiny">
                      {target.contact_id}
                    </div>
                    <div className="mt-xs text-tiny text-danger">
                      {target.error || 'Action failed'}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {loading && !items.length && <Loading />}
          {!loading && !items.length && (
            <EmptyMessage>No notifications yet.</EmptyMessage>
          )}
          {items.map(notification => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              type="regular"
              onShowFailures={setFailureJobId}
            />
          ))}
        </div>
      </Drawer>
    </>
  );
};

export default React.memo(NotificationsFeed) as typeof NotificationsFeed;
